/**
 * Transient toast channel (build-plan §7.3 / §7.9 — no native popups, ever).
 *
 * A tiny standalone Zustand store so non-React code (the builder reducers, e.g. an `addBlock` rejected
 * at a block cap) can surface feedback without a `window.alert`. The `ToastHost` subscribes and renders
 * an `aria-live` region; reducers just call `pushToast(...)`. Kept separate from `builderStore` so a
 * toast push never schedules autosave or touches undo history.
 */
import { create } from 'zustand';

export type ToastTone = 'info' | 'error' | 'success';

export interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastState {
  toasts: Toast[];
  push: (message: string, tone?: ToastTone) => void;
  dismiss: (id: number) => void;
}

let seq = 0;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push(message, tone = 'info') {
    const id = ++seq;
    set((s) => ({ toasts: [...s.toasts, { id, message, tone }] }));
    // Auto-dismiss; assertive errors linger a touch longer than info.
    const ttl = tone === 'error' ? 6000 : 3500;
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), ttl);
  },
  dismiss(id) {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
}));

/** Imperative push for non-React callers (the reducers). */
export function pushToast(message: string, tone: ToastTone = 'info'): void {
  useToastStore.getState().push(message, tone);
}
